/**
 * MultiSelectOperationService - Phase 3.2: Advanced Multi-Select Operations
 *
 * Provides algorithms for:
 * - Component alignment (left, right, top, bottom, center)
 * - Component distribution (even spacing, fixed gaps)
 * - Size matching (match widths, heights, or both)
 * - Stacking (vertical/horizontal with configurable gaps)
 * - Type-specific batch operations
 *
 * Phase 6 Integration:
 * - Detects when selected components are flex children
 * - Delegates flex-specific commands to FlexRelationshipService
 * - Provides flex-aware suggestions
 */

import { Component, ComponentType } from "@/types";
// Phase 6: Import FlexRelationshipService for parent-child relationship awareness
import { flexRelationshipService, FlexRelationship, FlexCommandTranslation } from "./FlexRelationshipService";

// ============================================================================
// Types
// ============================================================================

export type AlignmentAxis = 'left' | 'right' | 'top' | 'bottom' | 'center-h' | 'center-v';
export type DistributionAxis = 'horizontal' | 'vertical';
export type MatchDimension = 'width' | 'height' | 'both';
export type MatchReference = 'largest' | 'smallest' | 'first' | 'average';

export interface PositionChange {
  id: string;
  x?: number;
  y?: number;
  deltaX?: number;
  deltaY?: number;
}

export interface SizeChange {
  id: string;
  width?: number | string;
  height?: number | string;
}

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
  centerX: number;
  centerY: number;
}

export interface MultiSelectOperationResult {
  success: boolean;
  positionChanges?: PositionChange[];
  sizeChanges?: SizeChange[];
  affectedComponents: string[];
  skippedComponents?: Array<{ id: string; reason: string }>;
  description: string;
  // Phase 6: Flex-specific fields
  isFlexChildOperation?: boolean;
  flexTranslation?: FlexCommandTranslation;
  flexRelationship?: FlexRelationship;
}

export interface MultiSelectOperation {
  type: 'align' | 'distribute' | 'match' | 'stack' | 'group';
  axis?: AlignmentAxis | DistributionAxis;
  dimension?: MatchDimension;
  spacing?: number;
  reference?: MatchReference;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if a component is a flex child (positioned by parent flex container)
 */
function isFlexChild(component: Component, allComponents?: Component[]): boolean {
  if (!component.parentId) return false;

  // If we have access to all components, check if parent is a flex container
  if (allComponents) {
    const parent = allComponents.find(c => c.id === component.parentId);
    if (parent && parent.type === 'flex-container') {
      return true;
    }
  }

  // Fallback: Check if component has no explicit position (typical of flex children)
  const hasExplicitPosition = component.position?.x !== undefined &&
                              component.position?.y !== undefined &&
                              (component.position.x !== 0 || component.position.y !== 0);

  // If has parentId but no explicit position, likely a flex child
  return !hasExplicitPosition;
}

/**
 * Get the X position from a component, handling various storage locations
 */
function getComponentX(component: Component): number {
  // Check position.x first (most common)
  if (component.position?.x !== undefined) {
    return component.position.x;
  }

  // Check responsive position
  const desktopPos = (component.responsivePosition as any)?.breakpoints?.desktop;
  if (desktopPos?.position?.x !== undefined) {
    return desktopPos.position.x;
  }
  if (desktopPos?.x !== undefined) {
    return desktopPos.x;
  }

  // Default fallback
  console.log(`[Phase 3.2] getComponentX: No position found for ${component.id}, using 0`);
  return 0;
}

/**
 * Get the Y position from a component, handling various storage locations
 */
function getComponentY(component: Component): number {
  // Check position.y first (most common)
  if (component.position?.y !== undefined) {
    return component.position.y;
  }

  // Check responsive position
  const desktopPos = (component.responsivePosition as any)?.breakpoints?.desktop;
  if (desktopPos?.position?.y !== undefined) {
    return desktopPos.position.y;
  }
  if (desktopPos?.y !== undefined) {
    return desktopPos.y;
  }

  // Default fallback
  console.log(`[Phase 3.2] getComponentY: No position found for ${component.id}, using 0`);
  return 0;
}

/**
 * Get the numeric width from a component, handling various storage locations
 */
function getComponentWidth(component: Component): number {
  // Check size.width first (most common)
  if (component.size?.width !== undefined) {
    if (typeof component.size.width === 'number') return component.size.width;
    const parsed = parseInt(String(component.size.width), 10);
    if (!isNaN(parsed)) return parsed;
  }

  // Check style.width
  if (component.style?.width !== undefined) {
    if (typeof component.style.width === 'number') return component.style.width;
    const parsed = parseInt(String(component.style.width), 10);
    if (!isNaN(parsed)) return parsed;
  }

  // Check responsive position
  const desktopStyle = (component.responsivePosition as any)?.breakpoints?.desktop?.style;
  if (desktopStyle?.width !== undefined) {
    if (typeof desktopStyle.width === 'number') return desktopStyle.width;
    const parsed = parseInt(String(desktopStyle.width), 10);
    if (!isNaN(parsed)) return parsed;
  }

  // Check responsive position constraints
  const desktopConstraints = (component.responsivePosition as any)?.breakpoints?.desktop?.constraints;
  if (desktopConstraints?.width !== undefined) {
    if (typeof desktopConstraints.width === 'number') return desktopConstraints.width;
    const parsed = parseInt(String(desktopConstraints.width), 10);
    if (!isNaN(parsed)) return parsed;
  }

  // Default fallback
  console.log(`[Phase 3.2] getComponentWidth: No width found for ${component.id}, using 100`);
  return 100;
}

/**
 * Get the numeric height from a component, handling various storage locations
 */
function getComponentHeight(component: Component): number {
  // Check size.height first (most common)
  if (component.size?.height !== undefined) {
    if (typeof component.size.height === 'number') return component.size.height;
    const parsed = parseInt(String(component.size.height), 10);
    if (!isNaN(parsed)) return parsed;
  }

  // Check style.height
  if (component.style?.height !== undefined) {
    if (typeof component.style.height === 'number') return component.style.height;
    const parsed = parseInt(String(component.style.height), 10);
    if (!isNaN(parsed)) return parsed;
  }

  // Check responsive position
  const desktopStyle = (component.responsivePosition as any)?.breakpoints?.desktop?.style;
  if (desktopStyle?.height !== undefined) {
    if (typeof desktopStyle.height === 'number') return desktopStyle.height;
    const parsed = parseInt(String(desktopStyle.height), 10);
    if (!isNaN(parsed)) return parsed;
  }

  // Check responsive position constraints
  const desktopConstraints = (component.responsivePosition as any)?.breakpoints?.desktop?.constraints;
  if (desktopConstraints?.height !== undefined) {
    if (typeof desktopConstraints.height === 'number') return desktopConstraints.height;
    const parsed = parseInt(String(desktopConstraints.height), 10);
    if (!isNaN(parsed)) return parsed;
  }

  // Default fallback
  console.log(`[Phase 3.2] getComponentHeight: No height found for ${component.id}, using 100`);
  return 100;
}

/**
 * Calculate the bounding box for a set of components
 */
function getBoundingBox(components: Component[]): BoundingBox {
  if (components.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0, right: 0, bottom: 0, centerX: 0, centerY: 0 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const component of components) {
    const x = getComponentX(component);
    const y = getComponentY(component);
    const width = getComponentWidth(component);
    const height = getComponentHeight(component);

    console.log(`[Phase 3.2] getBoundingBox: Component ${component.id} at (${x}, ${y}) size ${width}x${height}`);

    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + width);
    maxY = Math.max(maxY, y + height);
  }

  const boxWidth = maxX - minX;
  const boxHeight = maxY - minY;

  const bounds = {
    x: minX,
    y: minY,
    width: boxWidth,
    height: boxHeight,
    right: maxX,
    bottom: maxY,
    centerX: minX + boxWidth / 2,
    centerY: minY + boxHeight / 2,
  };

  console.log(`[Phase 3.2] getBoundingBox: Bounds calculated:`, bounds);

  return bounds;
}

// ============================================================================
// MultiSelectOperationService Class
// ============================================================================

class MultiSelectOperationService {
  // ==========================================================================
  // Alignment Operations
  // ==========================================================================

  /**
   * Align components along a specified axis
   * @param components Components to align
   * @param axis Alignment axis (left, right, top, bottom, center-h, center-v)
   * @param allComponents Optional array of all components for parent lookup
   */
  alignComponents(components: Component[], axis: AlignmentAxis, allComponents?: Component[]): MultiSelectOperationResult {
    if (components.length < 2) {
      console.log('[Phase 3.2] alignComponents: Need at least 2 components to align');
      return {
        success: false,
        affectedComponents: [],
        description: 'Need at least 2 components to align',
      };
    }

    console.log(`[Phase 3.2] alignComponents: Aligning ${components.length} components to ${axis}`);
    console.log(`[Phase 3.2] alignComponents: Components:`, components.map(c => ({
      id: c.id,
      name: c.name,
      type: c.type,
      position: c.position,
      size: c.size,
      parentId: c.parentId,
    })));

    // Check if components are flex children
    const flexChildren = components.filter(c => isFlexChild(c, allComponents));
    if (flexChildren.length > 0) {
      console.log(`[Phase 6.2] alignComponents: ${flexChildren.length} components are flex children`);

      // Check if all selected components share the same flex parent
      const parentIds = new Set(flexChildren.map(c => c.parentId));
      if (parentIds.size === 1 && flexChildren.length === components.length) {
        const parentId = flexChildren[0].parentId;
        console.log(`[Phase 6.2] alignComponents: All components are children of flex container ${parentId}`);

        // Phase 6: Analyze flex relationship and provide translation info
        const flexRelationship = flexRelationshipService.analyzeMultipleFlexRelationships(components, allComponents || []);

        if (flexRelationship && flexRelationship.isFlexChild) {
          // Map alignment axis to command for flex translation
          const axisToCommand: Record<AlignmentAxis, string> = {
            'left': 'align left',
            'right': 'align right',
            'center-h': 'align center',
            'top': 'top align',
            'bottom': 'bottom align',
            'center-v': 'center vertically',
          };

          const command = axisToCommand[axis];
          const translation = flexRelationshipService.translateCommand(command, flexRelationship);

          if (translation) {
            console.log('[Phase 6.2] Delegating to flex handler:', {
              operationType: 'align',
              axis,
              translation,
            });

            return {
              success: true,
              affectedComponents: [parentId!],
              description: translation.description,
              isFlexChildOperation: true,
              flexTranslation: translation,
              flexRelationship,
            };
          }
        }

        // Fallback: Return guidance if flex translation not available
        const flexSuggestions: Record<AlignmentAxis, string> = {
          'left': 'justify-content: flex-start',
          'right': 'justify-content: flex-end',
          'center-h': 'justify-content: center',
          'top': 'align-items: flex-start',
          'bottom': 'align-items: flex-end',
          'center-v': 'align-items: center',
        };

        return {
          success: false,
          affectedComponents: [],
          description: `These components are inside a flex container. To align them, select the parent container and change its "${flexSuggestions[axis]}" property instead.`,
          skippedComponents: flexChildren.map(c => ({
            id: c.id,
            reason: 'Flex child - positioned by parent container'
          })),
        };
      }
    }

    const positionChanges: PositionChange[] = [];
    const bounds = getBoundingBox(components);

    for (const component of components) {
      const x = getComponentX(component);
      const y = getComponentY(component);
      const width = getComponentWidth(component);
      const height = getComponentHeight(component);

      console.log(`[Phase 3.2] alignComponents: Processing ${component.id} at (${x}, ${y}) size ${width}x${height}`);

      let newX = x;
      let newY = y;

      switch (axis) {
        case 'left':
          newX = bounds.x;
          break;
        case 'right':
          newX = bounds.right - width;
          break;
        case 'top':
          newY = bounds.y;
          break;
        case 'bottom':
          newY = bounds.bottom - height;
          break;
        case 'center-h':
          newX = bounds.centerX - width / 2;
          break;
        case 'center-v':
          newY = bounds.centerY - height / 2;
          break;
      }

      console.log(`[Phase 3.2] alignComponents: ${component.id} new position would be (${newX}, ${newY})`);

      // Only add change if position actually changed (with small tolerance for floating point)
      const xChanged = Math.abs(newX - x) > 0.5;
      const yChanged = Math.abs(newY - y) > 0.5;

      if (xChanged || yChanged) {
        positionChanges.push({
          id: component.id,
          x: Math.round(newX),
          y: Math.round(newY),
          deltaX: Math.round(newX - x),
          deltaY: Math.round(newY - y),
        });
        console.log(`[Phase 3.2] alignComponents: Adding change for ${component.id}: (${x}, ${y}) -> (${Math.round(newX)}, ${Math.round(newY)})`);
      } else {
        console.log(`[Phase 3.2] alignComponents: No change needed for ${component.id} (already aligned)`);
      }
    }

    const axisNames: Record<AlignmentAxis, string> = {
      'left': 'left edge',
      'right': 'right edge',
      'top': 'top edge',
      'bottom': 'bottom edge',
      'center-h': 'horizontal center',
      'center-v': 'vertical center',
    };

    console.log(`[Phase 3.2] alignComponents: Result - ${positionChanges.length} position changes`);

    return {
      success: true,
      positionChanges,
      affectedComponents: positionChanges.map(c => c.id),
      description: `Aligned ${positionChanges.length} components to ${axisNames[axis]}`,
    };
  }

  // ==========================================================================
  // Distribution Operations
  // ==========================================================================

  /**
   * Distribute components evenly along an axis
   * @param components Components to distribute
   * @param axis Distribution axis (horizontal or vertical)
   * @param spacing Optional fixed spacing between components (if not provided, distributes evenly)
   * @param allComponents Optional array of all components for parent lookup
   */
  distributeComponents(
    components: Component[],
    axis: DistributionAxis,
    spacing?: number,
    allComponents?: Component[]
  ): MultiSelectOperationResult {
    if (components.length < 3 && spacing === undefined) {
      console.log('[Phase 3.2] distributeComponents: Need at least 3 components for even distribution');
      return {
        success: false,
        affectedComponents: [],
        description: 'Need at least 3 components for even distribution (or use fixed spacing with 2+)',
      };
    }

    if (components.length < 2) {
      return {
        success: false,
        affectedComponents: [],
        description: 'Need at least 2 components to distribute',
      };
    }

    console.log(`[Phase 6.2] distributeComponents: Distributing ${components.length} components ${axis}ly${spacing !== undefined ? ` with ${spacing}px spacing` : ' evenly'}`);

    // Check if components are flex children
    const flexChildren = components.filter(c => isFlexChild(c, allComponents));
    if (flexChildren.length > 0 && flexChildren.length === components.length) {
      const parentIds = new Set(flexChildren.map(c => c.parentId));
      if (parentIds.size === 1) {
        const parentId = flexChildren[0].parentId;
        console.log(`[Phase 6.2] distributeComponents: All components are flex children of ${parentId}`);

        // Phase 6: Analyze flex relationship and provide translation info
        const flexRelationship = flexRelationshipService.analyzeMultipleFlexRelationships(components, allComponents || []);

        if (flexRelationship && flexRelationship.isFlexChild) {
          const command = spacing !== undefined ? `gap ${spacing}` : 'distribute evenly';
          const translation = flexRelationshipService.translateCommand(command, flexRelationship);

          if (translation) {
            console.log('[Phase 6.2] Delegating to flex handler:', {
              operationType: 'distribute',
              axis,
              spacing,
              translation,
            });

            return {
              success: true,
              affectedComponents: [parentId!],
              description: translation.description,
              isFlexChildOperation: true,
              flexTranslation: translation,
              flexRelationship,
            };
          }
        }

        // Fallback: Return guidance if flex translation not available
        return {
          success: false,
          affectedComponents: [],
          description: `These components are inside a flex container. To distribute them, select the parent container and adjust its "gap" or "justify-content: space-between" property instead.`,
          skippedComponents: flexChildren.map(c => ({
            id: c.id,
            reason: 'Flex child - spacing controlled by parent container'
          })),
        };
      }
    }

    // Sort components by position along the distribution axis
    const sorted = [...components].sort((a, b) => {
      if (axis === 'horizontal') {
        return getComponentX(a) - getComponentX(b);
      }
      return getComponentY(a) - getComponentY(b);
    });

    const positionChanges: PositionChange[] = [];
    const bounds = getBoundingBox(components);

    if (spacing !== undefined) {
      // Fixed spacing distribution
      let currentPos = axis === 'horizontal' ? getComponentX(sorted[0]) : getComponentY(sorted[0]);

      for (let i = 0; i < sorted.length; i++) {
        const component = sorted[i];
        const x = getComponentX(component);
        const y = getComponentY(component);
        const size = axis === 'horizontal' ? getComponentWidth(component) : getComponentHeight(component);

        const newX = axis === 'horizontal' ? currentPos : x;
        const newY = axis === 'vertical' ? currentPos : y;

        const xChanged = Math.abs(newX - x) > 0.5;
        const yChanged = Math.abs(newY - y) > 0.5;

        if (xChanged || yChanged) {
          positionChanges.push({
            id: component.id,
            x: Math.round(newX),
            y: Math.round(newY),
            deltaX: Math.round(newX - x),
            deltaY: Math.round(newY - y),
          });
          console.log(`[Phase 3.2] distributeComponents: ${component.id}: (${x}, ${y}) -> (${Math.round(newX)}, ${Math.round(newY)})`);
        }

        currentPos += size + spacing;
      }
    } else {
      // Even distribution
      const totalSize = sorted.reduce((sum, c) => {
        return sum + (axis === 'horizontal' ? getComponentWidth(c) : getComponentHeight(c));
      }, 0);

      const availableSpace = axis === 'horizontal' ? bounds.width : bounds.height;
      const totalGap = availableSpace - totalSize;
      const gapSize = totalGap / (sorted.length - 1);

      let currentPos = axis === 'horizontal' ? bounds.x : bounds.y;

      for (let i = 0; i < sorted.length; i++) {
        const component = sorted[i];
        const x = getComponentX(component);
        const y = getComponentY(component);
        const size = axis === 'horizontal' ? getComponentWidth(component) : getComponentHeight(component);

        const newX = axis === 'horizontal' ? currentPos : x;
        const newY = axis === 'vertical' ? currentPos : y;

        const xChanged = Math.abs(newX - x) > 0.5;
        const yChanged = Math.abs(newY - y) > 0.5;

        if (xChanged || yChanged) {
          positionChanges.push({
            id: component.id,
            x: Math.round(newX),
            y: Math.round(newY),
            deltaX: Math.round(newX - x),
            deltaY: Math.round(newY - y),
          });
          console.log(`[Phase 3.2] distributeComponents: ${component.id}: (${x}, ${y}) -> (${Math.round(newX)}, ${Math.round(newY)})`);
        }

        currentPos += size + gapSize;
      }
    }

    console.log(`[Phase 3.2] distributeComponents: Result - ${positionChanges.length} position changes`);

    return {
      success: true,
      positionChanges,
      affectedComponents: positionChanges.map(c => c.id),
      description: spacing !== undefined
        ? `Distributed ${components.length} components with ${spacing}px spacing`
        : `Distributed ${components.length} components evenly`,
    };
  }

  // ==========================================================================
  // Size Matching Operations
  // ==========================================================================

  /**
   * Match dimensions of components to a reference
   * @param components Components to resize
   * @param dimension Which dimension to match (width, height, or both)
   * @param reference How to determine the target size (largest, smallest, first, average)
   */
  matchDimension(
    components: Component[],
    dimension: MatchDimension,
    reference: MatchReference = 'largest'
  ): MultiSelectOperationResult {
    if (components.length < 2) {
      console.log('[Phase 3.2] matchDimension: Need at least 2 components to match sizes');
      return {
        success: false,
        affectedComponents: [],
        description: 'Need at least 2 components to match sizes',
      };
    }

    console.log(`[Phase 3.2] matchDimension: Matching ${dimension} of ${components.length} components using ${reference}`);

    // Calculate target dimensions based on reference
    let targetWidth: number | undefined;
    let targetHeight: number | undefined;

    const widths = components.map(c => getComponentWidth(c));
    const heights = components.map(c => getComponentHeight(c));

    switch (reference) {
      case 'largest':
        if (dimension === 'width' || dimension === 'both') {
          targetWidth = Math.max(...widths);
        }
        if (dimension === 'height' || dimension === 'both') {
          targetHeight = Math.max(...heights);
        }
        break;
      case 'smallest':
        if (dimension === 'width' || dimension === 'both') {
          targetWidth = Math.min(...widths);
        }
        if (dimension === 'height' || dimension === 'both') {
          targetHeight = Math.min(...heights);
        }
        break;
      case 'first':
        if (dimension === 'width' || dimension === 'both') {
          targetWidth = widths[0];
        }
        if (dimension === 'height' || dimension === 'both') {
          targetHeight = heights[0];
        }
        break;
      case 'average':
        if (dimension === 'width' || dimension === 'both') {
          targetWidth = Math.round(widths.reduce((a, b) => a + b, 0) / widths.length);
        }
        if (dimension === 'height' || dimension === 'both') {
          targetHeight = Math.round(heights.reduce((a, b) => a + b, 0) / heights.length);
        }
        break;
    }

    const sizeChanges: SizeChange[] = [];

    for (const component of components) {
      const currentWidth = getComponentWidth(component);
      const currentHeight = getComponentHeight(component);

      const change: SizeChange = { id: component.id };
      let hasChange = false;

      if (targetWidth !== undefined && currentWidth !== targetWidth) {
        change.width = targetWidth;
        hasChange = true;
      }
      if (targetHeight !== undefined && currentHeight !== targetHeight) {
        change.height = targetHeight;
        hasChange = true;
      }

      if (hasChange) {
        sizeChanges.push(change);
        console.log(`[Phase 3.2] Component ${component.id}: ${currentWidth}x${currentHeight} -> ${change.width || currentWidth}x${change.height || currentHeight}`);
      }
    }

    const dimensionNames: Record<MatchDimension, string> = {
      width: 'widths',
      height: 'heights',
      both: 'sizes',
    };

    return {
      success: true,
      sizeChanges,
      affectedComponents: sizeChanges.map(c => c.id),
      description: `Matched ${dimensionNames[dimension]} of ${sizeChanges.length} components (${reference}: ${targetWidth || '-'}x${targetHeight || '-'})`,
    };
  }

  // ==========================================================================
  // Stacking Operations
  // ==========================================================================

  /**
   * Stack components vertically or horizontally with a specified gap
   * @param components Components to stack
   * @param axis Stack direction (horizontal or vertical)
   * @param gap Gap between components in pixels
   * @param allComponents Optional array of all components for parent lookup
   */
  stackComponents(
    components: Component[],
    axis: DistributionAxis = 'vertical',
    gap: number = 16,
    allComponents?: Component[]
  ): MultiSelectOperationResult {
    if (components.length < 2) {
      console.log('[Phase 3.2] stackComponents: Need at least 2 components to stack');
      return {
        success: false,
        affectedComponents: [],
        description: 'Need at least 2 components to stack',
      };
    }

    console.log(`[Phase 6.2] stackComponents: Stacking ${components.length} components ${axis}ly with ${gap}px gap`);

    // Check if components are flex children
    const flexChildren = components.filter(c => isFlexChild(c, allComponents));
    if (flexChildren.length > 0 && flexChildren.length === components.length) {
      const parentIds = new Set(flexChildren.map(c => c.parentId));
      if (parentIds.size === 1) {
        const parentId = flexChildren[0].parentId;
        console.log(`[Phase 6.2] stackComponents: All components are flex children of ${parentId}`);

        // Phase 6: Analyze flex relationship and provide translation info
        const flexRelationship = flexRelationshipService.analyzeMultipleFlexRelationships(components, allComponents || []);

        if (flexRelationship && flexRelationship.isFlexChild) {
          const command = `stack with ${gap}px gap`;
          const translation = flexRelationshipService.translateCommand(command, flexRelationship);

          if (translation) {
            console.log('[Phase 6.2] Delegating to flex handler:', {
              operationType: 'stack',
              axis,
              gap,
              translation,
            });

            return {
              success: true,
              affectedComponents: [parentId!],
              description: translation.description,
              isFlexChildOperation: true,
              flexTranslation: translation,
              flexRelationship,
            };
          }
        }

        // Fallback: Return guidance if flex translation not available
        const directionProp = axis === 'vertical' ? 'flex-direction: column' : 'flex-direction: row';
        return {
          success: false,
          affectedComponents: [],
          description: `These components are inside a flex container. To stack them, select the parent container and set "${directionProp}" with "gap: ${gap}px".`,
          skippedComponents: flexChildren.map(c => ({
            id: c.id,
            reason: 'Flex child - layout controlled by parent container'
          })),
        };
      }
    }

    // Sort components by current position
    const sorted = [...components].sort((a, b) => {
      if (axis === 'horizontal') {
        return getComponentX(a) - getComponentX(b);
      }
      return getComponentY(a) - getComponentY(b);
    });

    const positionChanges: PositionChange[] = [];

    // Start from the first component's position
    let currentPos = axis === 'horizontal'
      ? getComponentX(sorted[0])
      : getComponentY(sorted[0]);

    // For horizontal stacking, keep the same Y; for vertical, keep the same X
    const referenceX = axis === 'vertical' ? getComponentX(sorted[0]) : undefined;
    const referenceY = axis === 'horizontal' ? getComponentY(sorted[0]) : undefined;

    for (let i = 0; i < sorted.length; i++) {
      const component = sorted[i];
      const x = getComponentX(component);
      const y = getComponentY(component);
      const size = axis === 'horizontal' ? getComponentWidth(component) : getComponentHeight(component);

      const newX = axis === 'horizontal' ? currentPos : (referenceX !== undefined ? referenceX : x);
      const newY = axis === 'vertical' ? currentPos : (referenceY !== undefined ? referenceY : y);

      const xChanged = Math.abs(newX - x) > 0.5;
      const yChanged = Math.abs(newY - y) > 0.5;

      if (xChanged || yChanged) {
        positionChanges.push({
          id: component.id,
          x: Math.round(newX),
          y: Math.round(newY),
          deltaX: Math.round(newX - x),
          deltaY: Math.round(newY - y),
        });
        console.log(`[Phase 3.2] stackComponents: ${component.id}: (${x}, ${y}) -> (${Math.round(newX)}, ${Math.round(newY)})`);
      }

      currentPos += size + gap;
    }

    console.log(`[Phase 3.2] stackComponents: Result - ${positionChanges.length} position changes`);

    return {
      success: true,
      positionChanges,
      affectedComponents: positionChanges.map(c => c.id),
      description: `Stacked ${components.length} components ${axis}ly with ${gap}px gaps`,
    };
  }

  // ==========================================================================
  // Type-Specific Batch Operations
  // ==========================================================================

  /**
   * Apply changes only to components of a specific type
   * @param components All selected components
   * @param targetTypes Component types to apply changes to
   * @param changes Property changes to apply
   */
  applyToMatchingTypes(
    components: Component[],
    targetTypes: ComponentType[],
    changes: Record<string, any>
  ): {
    applicable: Component[];
    skipped: Array<{ id: string; type: string; reason: string }>;
    description: string;
  } {
    const applicable: Component[] = [];
    const skipped: Array<{ id: string; type: string; reason: string }> = [];

    for (const component of components) {
      if (targetTypes.includes(component.type)) {
        applicable.push(component);
      } else {
        skipped.push({
          id: component.id,
          type: component.type,
          reason: `Not a ${targetTypes.join(' or ')} component`,
        });
      }
    }

    console.log(`[Phase 3.2] applyToMatchingTypes: ${applicable.length} applicable, ${skipped.length} skipped`);

    return {
      applicable,
      skipped,
      description: skipped.length > 0
        ? `Applied to ${applicable.length} ${targetTypes.join('/')} components. ${skipped.length} components skipped (not applicable).`
        : `Applied to ${applicable.length} ${targetTypes.join('/')} components.`,
    };
  }

  // ==========================================================================
  // Command Parsing
  // ==========================================================================

  /**
   * Parse a multi-select command and return the operation to perform
   */
  parseMultiSelectCommand(command: string): MultiSelectOperation | null {
    const normalizedCommand = command.trim().toLowerCase();

    // Alignment patterns - flexible to handle natural language like "align both components to the left"
    if (/align.*(?:to\s*(?:the\s*)?)?left/i.test(normalizedCommand)) {
      return { type: 'align', axis: 'left' };
    }

    if (/align.*(?:to\s*(?:the\s*)?)?right/i.test(normalizedCommand)) {
      return { type: 'align', axis: 'right' };
    }

    if (/align.*(?:to\s*(?:the\s*)?)?top/i.test(normalizedCommand)) {
      return { type: 'align', axis: 'top' };
    }

    if (/align.*(?:to\s*(?:the\s*)?)?bottom/i.test(normalizedCommand)) {
      return { type: 'align', axis: 'bottom' };
    }

    // Check for vertical alignment before generic center (so "align vertically" is caught)
    if (/align.*vertically/i.test(normalizedCommand)) {
      return { type: 'align', axis: 'center-h' }; // Align vertically means same horizontal center
    }

    // Check for horizontal alignment before generic center (so "align horizontally" is caught)
    if (/align.*horizontally/i.test(normalizedCommand)) {
      return { type: 'align', axis: 'center-v' }; // Align horizontally means same vertical center
    }

    if (/align.*(?:to\s*(?:the\s*)?)?(center|middle)/i.test(normalizedCommand)) {
      return { type: 'align', axis: 'center-h' }; // Default to horizontal center
    }

    // Distribution patterns - flexible
    if (/(distribute|space).*horizontally/i.test(normalizedCommand)) {
      return { type: 'distribute', axis: 'horizontal' };
    }

    if (/(distribute|space).*vertically/i.test(normalizedCommand)) {
      return { type: 'distribute', axis: 'vertical' };
    }

    if (/(distribute|space).*(?:evenly|equally)/i.test(normalizedCommand)) {
      return { type: 'distribute', axis: 'horizontal' }; // Default to horizontal
    }

    // Size matching patterns - flexible
    if (/(match|same|equal).*widths?/i.test(normalizedCommand) || /make.*same\s*width/i.test(normalizedCommand)) {
      return { type: 'match', dimension: 'width', reference: 'largest' };
    }

    if (/(match|same|equal).*heights?/i.test(normalizedCommand) || /make.*same\s*height/i.test(normalizedCommand)) {
      return { type: 'match', dimension: 'height', reference: 'largest' };
    }

    if (/(match|same|equal).*sizes?/i.test(normalizedCommand) || /make.*same\s*size/i.test(normalizedCommand)) {
      return { type: 'match', dimension: 'both', reference: 'largest' };
    }

    // Stack patterns with gap extraction
    const stackGapMatch = normalizedCommand.match(/stack.*?(\d+)\s*(?:px)?\s*(?:gaps?|spacing)/i);
    if (stackGapMatch) {
      const gap = parseInt(stackGapMatch[1], 10);
      const axis = /horizontally/i.test(normalizedCommand) ? 'horizontal' : 'vertical';
      return { type: 'stack', axis, spacing: gap };
    }

    if (/stack.*horizontally/i.test(normalizedCommand)) {
      return { type: 'stack', axis: 'horizontal', spacing: 16 };
    }

    if (/stack/i.test(normalizedCommand) && !/horizontal/i.test(normalizedCommand)) {
      return { type: 'stack', axis: 'vertical', spacing: 16 };
    }

    // Group patterns (for future implementation)
    if (/^group\s*(these|selected|all|them)?$/i.test(normalizedCommand)) {
      return { type: 'group' };
    }

    console.log(`[Phase 3.2] parseMultiSelectCommand: No pattern matched for "${command}"`);
    return null;
  }

  /**
   * Check if a command is a multi-select operation
   */
  isMultiSelectCommand(command: string): boolean {
    return this.parseMultiSelectCommand(command) !== null;
  }

  /**
   * Execute a multi-select operation
   * @param operation The operation to execute
   * @param components The selected components
   * @param allComponents Optional array of all components for parent/sibling lookup
   */
  executeOperation(
    operation: MultiSelectOperation,
    components: Component[],
    allComponents?: Component[]
  ): MultiSelectOperationResult {
    console.log(`[Phase 3.2] executeOperation: type=${operation.type}, components=${components.length}`);

    switch (operation.type) {
      case 'align':
        return this.alignComponents(components, operation.axis as AlignmentAxis, allComponents);

      case 'distribute':
        return this.distributeComponents(components, operation.axis as DistributionAxis, operation.spacing, allComponents);

      case 'match':
        return this.matchDimension(
          components,
          operation.dimension || 'both',
          operation.reference || 'largest'
        );

      case 'stack':
        return this.stackComponents(
          components,
          operation.axis as DistributionAxis || 'vertical',
          operation.spacing ?? 16,
          allComponents
        );

      case 'group':
        // Grouping is a placeholder for future implementation
        return {
          success: false,
          affectedComponents: [],
          description: 'Grouping functionality is not yet implemented. Components can be grouped manually using containers.',
        };

      default:
        return {
          success: false,
          affectedComponents: [],
          description: `Unknown operation type: ${operation.type}`,
        };
    }
  }
}

export const multiSelectOperationService = new MultiSelectOperationService();
